import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Wallet, Clock, CheckCircle, Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import { getAllMembers } from "../../../Redux/ApiCalls/Dashboard/MembersAPI";
import {
  getTeamMemberPaymentsAPI,
  type TeamMemberPaymentFilterParams,
} from "../../../Redux/ApiCalls/Dashboard/TeamMemberPaymentApi";
import CreateManualPaymentModal from "../../atoms/Dashboard/TeamMemberPayment/CreateManualPaymentModal";
import TeamMemberPaymentFilter from "../../atoms/Dashboard/TeamMemberPayment/TeamMemberPaymentFilter";
import TeamMemberPaymentList from "../../atoms/Dashboard/TeamMemberPayment/TeamMemberPaymentList";
import UpdatePaymentModal from "../../atoms/Dashboard/TeamMemberPayment/UpdatePaymentModal";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { deleteTeamMemberPayment } from "../../../Redux/ApiCalls/Dashboard/TeamMemberPaymentApi";
import { MarketplaceLoadingSpinner } from "./Marketplace/MarketplaceEmptyState";
import { device } from "../../atoms/Utlis/media";
import PermissionGate from "../../atoms/Utlis/PermissionGate";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
}

const defaultFilters: TeamMemberPaymentFilterParams = {
  page: 1,
  page_size: 15,
};

export default function TeamMemberPaymentPage() {
  const dispatch = useAppDispatch();
  const { members, loading: membersLoading, isMemberFetched } = useAppSelector((state) => state.member);
  const {
    payments,
    team_member_payments: summary,
    loading: paymentsLoading,
    count,
    total_pages,
    current_page,
  } = useAppSelector((state) => state.teamMemberPayment);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updatePaymentId, setUpdatePaymentId] = useState<number | string | null>(null);
  const [deletePaymentId, setDeletePaymentId] = useState<number | string | null>(null);
  const [filters, setFilters] = useState<TeamMemberPaymentFilterParams>(defaultFilters);

  useEffect(() => {
    if (!isMemberFetched) {
      dispatch(getAllMembers());
    }
  }, [dispatch, isMemberFetched]);

  const fetchPayments = useCallback(
    (override?: TeamMemberPaymentFilterParams) => {
      dispatch(getTeamMemberPaymentsAPI(override ?? filters));
    },
    [dispatch, filters]
  );

  useEffect(() => {
    dispatch(getTeamMemberPaymentsAPI(defaultFilters));
  }, [dispatch]);

  const handleApply = (override?: TeamMemberPaymentFilterParams) => {
    const next = override ?? { ...filters, page: 1 };
    setFilters(next);
    dispatch(getTeamMemberPaymentsAPI(next));
  };

  const handlePageChange = (page: number) => {
    const next = { ...filters, page };
    setFilters(next);
    dispatch(getTeamMemberPaymentsAPI(next));
  };

  const handlePaymentCreated = () => {
    fetchPayments();
  };

  const handlePaymentUpdated = () => {
    setUpdatePaymentId(null);
    fetchPayments();
  };

  const handleDeletePayment = async () => {
    if (deletePaymentId == null) return;
    const result = await deleteTeamMemberPayment(deletePaymentId);
    if (result != null) {
      setDeletePaymentId(null);
      fetchPayments();
    }
  };

  const isLoading = membersLoading && !members?.length;

  return (
    <PageInner>
      <Toolbar>
        <ToolbarText>
          <SectionTitle>Team payouts</SectionTitle>
          <SectionHint>Record and track manual payments for your team members</SectionHint>
        </ToolbarText>
        <PermissionGate permission="payments_management.create">
          <CreateButton
            type="button"
            onClick={() => setShowCreateModal(true)}
            disabled={membersLoading}
          >
            <Plus size={16} strokeWidth={2.4} />
            Create Manual Payment
          </CreateButton>
        </PermissionGate>
      </Toolbar>

      {isLoading ? (
        <MarketplaceLoadingSpinner padding="48px 22px" />
      ) : (
        <>
          {summary && (
            <StatsGrid>
              <WalletHero>
                <WalletIconWrap>
                  <Wallet size={16} />
                </WalletIconWrap>
                <WalletHeroBody>
                  <WalletHeroValue>{formatCurrency(summary.total_payments)}</WalletHeroValue>
                  <WalletHeroLabel>Total Payments</WalletHeroLabel>
                </WalletHeroBody>
              </WalletHero>

              <StatCard $tone="#f2ad6d">
                <StatIcon $tone="#f2ad6d">
                  <Clock size={16} />
                </StatIcon>
                <StatContent>
                  <StatValue>{formatCurrency(summary.payments_pending.total)}</StatValue>
                  <StatLabel>Pending</StatLabel>
                </StatContent>
              </StatCard>

              <StatCard $tone="#31a66a">
                <StatIcon $tone="#31a66a">
                  <CheckCircle size={16} />
                </StatIcon>
                <StatContent>
                  <StatValue>{formatCurrency(summary.payments_completed.total)}</StatValue>
                  <StatLabel>Received</StatLabel>
                </StatContent>
              </StatCard>
            </StatsGrid>
          )}

          <TeamMemberPaymentFilter
            members={members ?? []}
            filters={filters}
            onFiltersChange={setFilters}
            onApply={handleApply}
            loading={paymentsLoading}
            totalPages={total_pages || 1}
            currentPage={current_page || 1}
            count={count ?? 0}
            onPageChange={handlePageChange}
          />

          {paymentsLoading && !payments?.length ? (
            <MarketplaceLoadingSpinner padding="48px 22px" />
          ) : (
            <TeamMemberPaymentList
              payments={payments ?? []}
              onUpdate={(id) => setUpdatePaymentId(id)}
              onDelete={(id) => setDeletePaymentId(id)}
            />
          )}
        </>
      )}

      <UpdatePaymentModal
        isOpen={updatePaymentId != null}
        onClose={() => setUpdatePaymentId(null)}
        paymentId={updatePaymentId}
        onSuccess={handlePaymentUpdated}
      />

      {deletePaymentId != null && (
        <DeletePopup
          Delete={handleDeletePayment}
          cancel={() => setDeletePaymentId(null)}
          text="Are you sure you want to delete this payment?"
          buttonText="Delete"
        />
      )}

      <CreateManualPaymentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        members={members ?? []}
        onSuccess={handlePaymentCreated}
      />
    </PageInner>
  );
}

const gradient = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const PageInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 22px 24px;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const ToolbarText = styled.div`
  min-width: 0;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
`;

const SectionHint = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease;
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media ${device.laptop} { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const StatValue = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
